import React from 'react';
import styled, { css } from 'styled-components';

/**
 * Shared layout primitives. Every page used to declare its own `Container` and
 * `Section` with slightly different gutters; these are the one definition.
 */

export const Container = styled.div<{ $narrow?: boolean }>`
  width: 100%;
  max-width: ${props => props.$narrow ? props.theme.layout.narrowWidth : props.theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing[5]};

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    padding: 0 ${props => props.theme.spacing[8]};
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    padding: 0 ${props => props.theme.spacing[10]};
  }
`;

export const Section = styled.section<{
  $surface?: 'white' | 'muted' | 'ink';
  $size?: 'sm' | 'md' | 'lg';
  $divider?: boolean;
}>`
  padding: ${props => props.theme.sectionPadding[props.$size ?? 'md']} 0;
  background: ${props => props.theme.colors.background};

  ${props => props.$surface === 'muted' && css`
    background: ${props.theme.colors.surface};
  `}

  ${props => props.$surface === 'ink' && css`
    background: ${props.theme.colors.primary[900]};
    color: ${props.theme.colors.neutral[300]};

    h1, h2, h3, h4, h5, h6 {
      color: ${props.theme.colors.white};
    }
  `}

  ${props => props.$divider && css`
    border-top: 1px solid ${props.theme.colors.border};
  `}
`;

const LabelRow = styled.div<{ $align: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  flex-direction: ${props => props.$align === 'right' ? 'row' : 'row-reverse'};
  margin-bottom: ${props => props.theme.spacing[10]};
`;

/** The hairline that runs out to the edge of the container. */
const Rule = styled.span`
  flex: 1;
  height: 1px;
  background-image: linear-gradient(
    to right,
    ${props => props.theme.colors.neutral[300]} 0 6px,
    transparent 6px 12px
  );
  background-size: 12px 1px;
  background-repeat: repeat-x;
`;

const LabelText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
  text-transform: uppercase;
  color: ${props => props.theme.colors.textMuted};
  white-space: nowrap;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${props => props.theme.borderRadius.full};
    background: ${props => props.theme.colors.primary[500]};
    flex-shrink: 0;
  }
`;

interface SectionLabelProps {
  children: React.ReactNode;
  /** Which end of the rule the label sits on. */
  align?: 'left' | 'right';
  className?: string;
}

/**
 * Small caps label tied to a dashed rule, used to open a section. Borrowed from
 * the reference layout — it gives a section a header without a second heading.
 */
export const SectionLabel: React.FC<SectionLabelProps> = ({
  children,
  align = 'right',
  className,
}) => (
  <LabelRow $align={align} className={className}>
    <Rule aria-hidden="true" />
    <LabelText>{children}</LabelText>
  </LabelRow>
);

/**
 * Placeholder shown while the content document is being fetched. Pages used to
 * render a bare, unstyled "Loading..." at the far left of the viewport.
 */
export const PageLoading: React.FC<{ label?: string }> = ({ label = 'Loading' }) => (
  <LoadingWrap>
    <LoadingDot aria-hidden="true" />
    <span>{label}</span>
  </LoadingWrap>
);

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[3]};
  min-height: 60vh;
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
  text-transform: uppercase;
  color: ${props => props.theme.colors.textMuted};
`;

const LoadingDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.theme.colors.primary[500]};
  animation: pulse 1.2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 0.25; }
    50% { opacity: 1; }
  }
`;

/** Standalone eyebrow, for when there is no rule to hang it from. */
export const Eyebrow = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
  text-transform: uppercase;
  color: ${props => props.theme.colors.textMuted};
  margin-bottom: ${props => props.theme.spacing[5]};
`;

export const SectionTitle = styled.h2`
  max-width: 18ch;
  margin-bottom: ${props => props.theme.spacing[6]};
`;

/** Introductory paragraph — one step up in size, muted, never full-width. */
export const Lede = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  line-height: ${props => props.theme.typography.lineHeight.relaxed};
  color: ${props => props.theme.colors.textMuted};
  max-width: 62ch;
`;

export default Section;
